import { amenities, AmenityGrid } from '../components/amenity';
import { tents } from '../constants/tents';
import { Section } from '../components/section';
import { sections } from '../hooks/use-section-analytics';
import Sprite from '../sprites/sprite';
import { GetInTouch } from '../components/get-in-touch/get-in-touch';
import { FAQ, faqs } from '../components/faq/faq';
import { TentExperienceOption } from '../components/booking-form/tent-experience-field';
import { FormattedString, useMessages } from '../i18n/useMessages';
import { onOpenTentModal } from '../state/tent-modal';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';

const tentList = Object.values(tents);
// Filter by isPrivate property instead of hardcoded slices
const privateTentList = tentList.filter((tent) => tent.isPrivate);
const dormTentList = tentList.filter((tent) => !tent.isPrivate);

export const AccommodationAndAmenitiesSection = () => {
  const getMessage = useMessages();
  const allAmenities = getMessage(
    'camp67-accommodation-and-amenities.amenities-included',
  );
  const chooseFromPackages = getMessage(
    'camp67-accommodation-and-amenities.choose-from-packages',
  );

  const privateTitle = getMessage('camp67-accommodation-and-amenities.private.title');
  const dormTitle = getMessage('camp67-accommodation-and-amenities.dorm.title');

  return (
    <Section
      className="py-16 md:py-20 lg:py-24 relative"
      id={sections['accommodationAmenities']}
    >
      {/* Subtle background with sun accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-orange-50/40 via-white to-white" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Sun decoration with better positioning */}
        <Sprite
          name="sun"
          width="210"
          height="175"
          className="hidden lg:block w-32 h-32 absolute -left-16 -top-8 text-orange-300 opacity-40"
        />

        {/* Header Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-block mb-3">
            <span className="text-orange-800 font-bold text-xs uppercase tracking-widest bg-orange-100 px-3 py-1.5 rounded-sm">
              {allAmenities}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-serif font-semibold text-gray-900 mb-6 leading-tight">
            <FormattedString
              message="camp67-accommodation-and-amenities.come-to-home"
              components={{
                'home-highlight': (
                  <span className="text-orange-600 relative italic">
                    <span className="absolute -inset-x-2 -inset-y-1 bg-orange-100 rounded-lg opacity-60 transform -rotate-1"></span>
                    <span className="relative">home</span>
                  </span>
                ),
              }}
            />
          </h2>
          <p className="text-lg lg:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto font-medium mb-8">
            {chooseFromPackages}
          </p>

          {/* Primary CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {isInquiryModeEnabled ? (
              <button
                onClick={() => onOpenInquiry()}
                className="group inline-flex items-center gap-3 bg-red-200 hover:bg-red-300 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg shadow-red-400/30 hover:shadow-xl transform hover:scale-105"
              >
                <span className="text-lg">Inquire Now</span>
                <svg
                  className="w-5 h-5 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </button>
            ) : (
              <a
                href="/packages/reservation"
                className="group inline-flex items-center gap-3 bg-red-200 hover:bg-red-300 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg shadow-red-400/30 hover:shadow-xl transform hover:scale-105"
              >
                <span className="text-lg">Book Your Stay Now</span>
                <svg
                  className="w-5 h-5 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </a>
            )}
            <button
              onClick={() =>
                document
                  .getElementById('contact-form')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
              className="group inline-flex items-center gap-2 text-orange-700 hover:text-orange-800 font-medium px-6 py-4 border-2 border-orange-200 hover:border-orange-300 rounded-xl transition-all duration-200 hover:bg-orange-50"
            >
              <span>Have Questions?</span>
              <svg
                className="w-4 h-4 transition-transform group-hover:translate-y-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Accommodation Options */}
        <div className="mb-16">
          {/* Dormitory Accommodations - 3 items, use 3 columns on lg */}
          <div>
            <div className="text-center mb-8">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 uppercase tracking-wider mb-2">
                {dormTitle}
              </h3>
              <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {dormTentList.map((tent) => (
                <div
                  key={tent.tentType}
                  className="transform hover:-translate-y-2 transition-transform duration-300"
                >
                  <TentExperienceOption
                    type={tent.tentType}
                    isDisabled={false}
                    isSelected={true}
                    showCounts={false}
                    onClick={() => {}}
                    onClickLearnMore={() => {
                      onOpenTentModal(tent.tentType);
                    }}
                    renderModal={false}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Private Accommodations - 2 items, centered 2-column layout */}
        <div className="mb-20">
          <div className="text-center mb-8">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 uppercase tracking-wider mb-2">
              {privateTitle}
            </h3>
            <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {privateTentList.map((tent) => (
              <div
                key={tent.tentType}
                className="transform hover:-translate-y-2 transition-transform duration-300"
              >
                <TentExperienceOption
                  type={tent.tentType}
                  isDisabled={false}
                  isSelected={true}
                  showCounts={false}
                  onClick={() => {}}
                  onClickLearnMore={() => {
                    onOpenTentModal(tent.tentType);
                  }}
                  renderModal={false}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Amenities Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-serif font-medium text-gray-900 mb-4">
              Premium Amenities & Services
            </h3>
            <p className="text-gray-600 text-sm lg:text-base max-w-2xl mx-auto">
              Everything you need for a comfortable and memorable Kumbh experience
            </p>
          </div>

          {/* Key Highlights - Always Visible */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {amenities.slice(0, 2).map((amenity, key) => {
              const summary = getMessage(amenity.summary);
              return (
                <div
                  key={key}
                  className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100"
                >
                  <h4 className="font-medium text-lg text-gray-900 mb-3 uppercase tracking-wide">
                    {summary}
                  </h4>
                  <div className="text-gray-700 text-base leading-relaxed">
                    {amenity.detail}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Other Amenities - Compact Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {amenities.slice(2).map((amenity, key) => (
              <div key={key + 2}>
                <AmenityGrid summary={amenity.summary} detail={amenity.detail} />
              </div>
            ))}
          </div>
        </div>

        {/* Secondary CTA after viewing accommodations */}
        <div className="bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 rounded-2xl p-8 mb-20 border border-blue-200">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl italic md:text-3xl font-serif font-semibold text-gray-900 mb-4">
              Ready to Secure Your Spot?
            </h3>
            <p className="text-gray-600 text-sm font-medium mb-2">
              Camp open: 31 Dec 2025 – End of Feb 2026
            </p>
            <p className="text-gray-700 text-lg mb-6">
              Limited spaces available for Magh Mela 2026.{' '}
              {isInquiryModeEnabled
                ? 'Register your interest now for priority access.'
                : 'Book now to guarantee your peaceful retreat.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {isInquiryModeEnabled ? (
                <button
                  onClick={() => onOpenInquiry()}
                  className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Register Interest</span>
                  <svg
                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </button>
              ) : (
                <a
                  href="/packages/reservation"
                  className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Check Availability & Book</span>
                  <svg
                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </a>
              )}
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  {isInquiryModeEnabled
                    ? 'Priority access • Quick response'
                    : 'Secure payment • Instant confirmation'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ and Contact Section */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* FAQs */}
          <div className="lg:col-span-3">
            <div className="mb-8">
              <h4 className="text-xl md:text-2xl lg:text-3xl font-serif font-medium text-gray-900 mb-2">
                Frequently Asked Questions
              </h4>
              <div className="w-16 h-1 bg-orange-400 rounded-full" />
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                >
                  <FAQ {...faq} />
                </div>
              ))}
            </div>
          </div>

          {/* Get in Touch - Wider sidebar */}
          <div className="lg:col-span-2">
            <div className="sticky top-8" id="contact-form">
              <GetInTouch />
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
